import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  Badge,
  Button,
  Modal,
  PageHeader,
  Spinner,
  Table,
  type BadgeVariant,
  type TableColumn,
} from "../ds";
import { api, errorText, type Backup } from "../lib/api";
import { fmtBytes, fmtDateTime } from "../lib/format";

// Alle Backups auf dem Server, routerübergreifend (`GET /api/backups`) — der
// Katalog je Router bleibt auf der Detailseite (System → Backups), hier geht es
// um den Bestand als Ganzes und den Sammel-Export: ein ZIP mit dem jeweils
// letzten Backup jedes Routers, entschlüsselt (docs/device-management.md →
// Backups). Der Export ist ein Direktlink — die Bytes baut der Server.

const STATUS: Record<Backup["status"], { variant: BadgeVariant; label: string }> = {
  pending: { variant: "warn", label: "wird geladen" },
  ok: { variant: "success", label: "vollständig" },
  error: { variant: "danger", label: "fehlerhaft" },
};

/** IDs der Backups, die der Sammel-Export mitnimmt: je Router das neueste `ok`. */
function exportedIds(rows: Backup[]): Set<string> {
  const newest = new Map<string, Backup>();
  for (const b of rows) {
    if (b.status !== "ok") continue;
    const seen = newest.get(b.deviceId);
    if (!seen || b.createdAt > seen.createdAt) newest.set(b.deviceId, b);
  }
  return new Set([...newest.values()].map((b) => b.id));
}

function columns(
  inExport: Set<string>,
  onDelete: (b: Backup) => void,
  busy: boolean,
): TableColumn<Backup>[] {
  return [
    {
      key: "router",
      header: "Router",
      cell: (b) => (
        <Link to={`/fleet/${b.deviceId}`} className="ds-link">
          {b.deviceHostname || b.deviceId}
        </Link>
      ),
    },
    { key: "created", header: "Erstellt", cell: (b) => fmtDateTime(b.createdAt) },
    { key: "size", header: "Größe", num: true, cell: (b) => fmtBytes(b.sizeBytes) },
    {
      key: "firmware",
      header: "Firmware damals",
      hideOnMobile: true,
      cell: (b) => b.firmwareVersion || "—",
    },
    { key: "trigger", header: "Auslöser", hideOnMobile: true, cell: (b) => b.trigger || "—" },
    {
      key: "status",
      header: "Status",
      cell: (b) => (
        <span className="flex flex-wrap gap-1">
          <Badge variant={STATUS[b.status].variant}>{STATUS[b.status].label}</Badge>
          {inExport.has(b.id) && <Badge variant="info">im Export</Badge>}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (b) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            iconName="download"
            title="Entschlüsselt herunterladen"
            disabled={b.status !== "ok"}
            onClick={() => window.location.assign(api.backupDownloadUrl(b.id))}
          />
          <Button
            size="sm"
            variant="ghost"
            iconName="trash"
            title="Backup löschen"
            disabled={busy}
            onClick={() => onDelete(b)}
          />
        </div>
      ),
    },
  ];
}

export function BackupsPage() {
  const [rows, setRows] = useState<Backup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Backup | null>(null);

  const laden = useCallback(() => {
    api
      .listBackups()
      .then(setRows)
      .catch((e: unknown) => setError(errorText(e)));
  }, []);
  useEffect(laden, [laden]);

  const loeschen = async () => {
    if (!deleting) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteBackup(deleting.id);
      setDeleting(null);
      laden();
    } catch (e: unknown) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const inExport = exportedIds(rows ?? []);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Backups"
        subtitle="Alle Config-Backups auf dem Server — verschlüsselt gespeichert, entschlüsselt exportiert."
        actions={
          <Button
            iconName="download"
            disabled={inExport.size === 0}
            onClick={() => window.location.assign(api.backupExportUrl)}
          >
            Sammel-Export (ZIP)
          </Button>
        }
      />

      {error && <Alert variant="danger">{error}</Alert>}
      <Alert variant="warning">
        Der Sammel-Export enthält das jeweils letzte Backup jedes Routers <b>unverschlüsselt</b> —
        inklusive WireGuard-Schlüsseln und Passwort-Hashes. Entsprechend ablegen.
      </Alert>

      {rows === null ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : (
        <Table
          columns={columns(inExport, setDeleting, busy)}
          rows={rows}
          rowKey={(b) => b.id}
          emptyMessage="Noch kein Backup im Katalog — Backups entstehen auf der Router-Seite (System) oder automatisch vor jedem Flash."
        />
      )}

      {deleting && (
        <Modal
          open
          onClose={() => setDeleting(null)}
          title="Backup löschen?"
          actions={
            <>
              <Button variant="ghost" onClick={() => setDeleting(null)} disabled={busy}>
                Abbrechen
              </Button>
              <Button variant="danger" loading={busy} onClick={() => void loeschen()}>
                Löschen
              </Button>
            </>
          }
        >
          <p>
            {deleting.deviceHostname || deleting.deviceId}, {fmtDateTime(deleting.createdAt)} —
            Datei und Katalogeintrag sind danach weg. Für einen Router ohne weiteres Backup gibt es
            dann keine Kopie seiner Konfiguration mehr.
          </p>
        </Modal>
      )}
    </div>
  );
}
